import { cn } from '@/lib/utils';

/**
 * Email-seeded avatar that renders sender initials on a warm-palette fill.
 * Uses the DESIGN.md report colors so tickets feel visually distinct without
 * competing with Fin Orange (which is reserved for AI / brand surfaces).
 */
const PALETTE = [
  { bg: '#ffe9d6', fg: '#7a2a00' }, // warm peach
  { bg: '#e5f2ff', fg: '#0a2b4a' }, // report blue tint
  { bg: '#e3f7e8', fg: '#0a3a16' }, // report green tint
  { bg: '#fde4ea', fg: '#75081f' }, // report pink tint
  { bg: '#f0e9d6', fg: '#4a3d0a' }, // warm sand
  { bg: '#ede3ff', fg: '#2f0a6b' }, // soft violet
];

function hashSeed(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return h;
}

function initialsFor(value: string): string {
  const base = value.includes('@') ? value.split('@')[0] : value;
  const tokens = base.split(/[._\-\s+]+/).filter(Boolean);
  if (tokens.length === 0) return value.slice(0, 2).toUpperCase();
  const first = tokens[0][0] ?? '';
  const second = tokens.length > 1 ? tokens[tokens.length - 1][0] ?? '' : (tokens[0][1] ?? '');
  return (first + second).toUpperCase().slice(0, 2);
}

export function Avatar({
  seed,
  label,
  size = 36,
  className,
}: {
  /** Stable identifier (usually the sender email). */
  seed: string;
  /** Optional display label; falls back to seed for initials. */
  label?: string;
  size?: number;
  className?: string;
}) {
  const palette = PALETTE[hashSeed(seed) % PALETTE.length];
  const initials = initialsFor(label ?? seed);
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex items-center justify-center rounded-full font-medium shrink-0',
        className,
      )}
      style={{
        width: size,
        height: size,
        background: palette.bg,
        color: palette.fg,
        fontSize: Math.max(11, Math.round(size * 0.38)),
        letterSpacing: '0.02em',
      }}
    >
      {initials || '•'}
    </span>
  );
}
